import React, { useState, useEffect, useCallback } from 'react';
import { authedFetch } from '../../utils/backendFetch';
import { Box, Typography, Button, CircularProgress, Chip, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import useAlert from '../../alert/useAlert.js';
import OrganizationPayoutSettings from '../../components/OrgPayouts/OrganizationPayoutSettings';
import { orgPayoutsEnabled } from '../../components/OrgPayouts/api';
import { useSelector } from 'react-redux';

// tikcare_rag backend (serves the /stripe blueprint). NOT the subscription microservice.
const STRIPE_CONNECT_URL = process.env.REACT_APP_STRIPE_CONNECT_URL;
// const STRIPE_CONNECT_URL = 'http://127.0.0.1:5000';

const STATUS_META = {
  not_started: { label: 'Not set up', color: 'default', button: 'Set up payouts with Stripe' },
  pending: { label: 'Pending verification', color: 'warning', button: 'Review payout details' },
  restricted: { label: 'Action needed', color: 'error', button: 'Continue payout setup' },
  complete: { label: 'Connected', color: 'success', button: 'Manage payout account' },
};

/**
 * Stripe Connect payout onboarding for therapists.
 *
 * Shows the current payout-account status and a single button that redirects the
 * therapist to Stripe's hosted onboarding. On return, the status is re-fetched.
 * Self-contained — independent of the profile form's save handler.
 * onStatusChange (optional) receives the status string so the parent can gate
 * features (e.g. referral sharing) on payout readiness.
 */
const PayoutSetup = ({ userid, onStatusChange }) => {
  const { setAlert } = useAlert();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!userid) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await authedFetch(`${STRIPE_CONNECT_URL}/stripe/connect/status/${userid}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to load payout status.');
      setStatus(data);
      if (onStatusChange) onStatusChange(data && data.status);
    } catch (err) {
      console.error('Failed to fetch payout status:', err);
      setStatus({ status: 'not_started' });
      if (onStatusChange) onStatusChange('not_started');
    } finally {
      setLoading(false);
    }
  }, [userid, onStatusChange]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSetup = async () => {
    if (!userid) return;
    try {
      setRedirecting(true);
      const returnUrl = window.location.href;
      const res = await authedFetch(`${STRIPE_CONNECT_URL}/stripe/connect/onboarding-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userid, return_url: returnUrl, refresh_url: returnUrl }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to open payout setup.');
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err) {
      console.error('Failed to start payout setup:', err);
      const msg = (err && err.response && err.response.data && err.response.data.error)
        || err.message || 'Failed to start payout setup. Please try again.';
      setAlert(msg, 'error');
      setRedirecting(false);
    }
  };

  const meta = STATUS_META[status && status.status] || STATUS_META.not_started;

  return (
    <Box id="payout-setup-section" sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 'bold', color: '#29C0BB', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <AccountBalanceWalletIcon fontSize="small" /> Get Paid
        </Typography>
        {!loading && <Chip size="small" label={meta.label} color={meta.color} />}
      </Stack>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Connect your bank account to receive payments for your sessions. You’ll be securely
        redirected to Stripe to provide your bank and identity details and complete the setup.
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>When you’ll be paid:</Box>{' '}
        Your total earnings for each calendar month are calculated at the end of that month
        and released at the beginning of the following month, typically a few days after the
        month ends. For example, your January earnings will be calculated on January 31 and
        released in early February.
      </Typography>

      {loading ? (
        <CircularProgress size={22} sx={{ color: '#29C0BB' }} />
      ) : (
        <>
          {status && status.status === 'restricted' && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 1 }}>
              Stripe needs more information before payouts can be enabled.
              {Array.isArray(status.requirements_due) && status.requirements_due.length > 0 &&
                ` Outstanding: ${status.requirements_due.join(', ')}`}
            </Typography>
          )}
          {status && status.status === 'pending' && (
            <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mb: 1 }}>
              Your details have been submitted — Stripe is verifying them. This usually takes a few
              minutes but can take up to a few business days. No action is needed from you; check
              back later or refresh the status.
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleSetup}
              disabled={redirecting}
              variant="contained"
              sx={{
                borderRadius: 30,
                backgroundColor: '#29C0BB',
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: '#22a8a3' },
              }}
            >
              {redirecting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : meta.button}
            </Button>
            {status && status.status !== 'not_started' && (
              <Button
                onClick={fetchStatus}
                disabled={redirecting}
                variant="text"
                size="small"
                startIcon={<RefreshIcon />}
                sx={{ textTransform: 'none', color: '#29C0BB' }}
              >
                Refresh status
              </Button>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default function PayoutSetupEntry(props) {
  const org = useSelector(state => state.globalState.currentOrg);
  if (orgPayoutsEnabled && org?.id) return <OrganizationPayoutSettings onStatusChange={props.onStatusChange} />;
  return <PayoutSetup {...props} />;
}
